import axiosInstance from './api';
import { mapPortalNotificationsPage } from '../utils/mapPortalNotification';
import { resolvePortalNotificationReadAction } from '../utils/notificationReadActions';

const getErrorMessage = (error, fallback) => error?.message || error?.error?.message || fallback;

/** Admin / partner panel notification API base (replaces mobile `/notifications`). */
export const ADMIN_NOTIFICATIONS_BASE = '/admin/notifications';

export const NOTIFICATION_AUDIENCE_PORTAL = 'portal';

/** Default page size for GET /admin/notifications?audience=portal&limit=&page= */
export const PORTAL_NOTIFICATIONS_PAGE_SIZE = 20;

const portalParams = (extra = {}) => ({
  audience: NOTIFICATION_AUDIENCE_PORTAL,
  ...extra,
});

/** POST /admin/notifications/register-device */
export const registerNotificationDevice = async ({ token, deviceInfo, audience }) => {
  try {
    await axiosInstance.post(`${ADMIN_NOTIFICATIONS_BASE}/register-device`, {
      token,
      audience: audience ?? NOTIFICATION_AUDIENCE_PORTAL,
      deviceInfo,
    });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to register device for notifications'),
    };
  }
};

/**
 * GET /admin/notifications?audience=portal&limit=20&page=1
 * Authorization: Bearer (via axiosInstance).
 */
export const fetchPortalNotifications = async ({
  limit = PORTAL_NOTIFICATIONS_PAGE_SIZE,
  page = 1,
  userRole = null,
} = {}) => {
  try {
    const { data } = await axiosInstance.get(ADMIN_NOTIFICATIONS_BASE, {
      params: portalParams({ limit, page }),
    });

    const mapped = mapPortalNotificationsPage(data, { limit, page, userRole });

    return {
      success: true,
      ...mapped,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to load notifications'),
      notifications: [],
      total: null,
      page,
      limit,
      hasMore: false,
    };
  }
};

/** PUT /admin/notifications/:id/read?audience=portal */
export const markPortalNotificationRead = async (notificationId) => {
  const id = notificationId ? String(notificationId).trim() : '';
  if (!id) {
    return { success: false, message: 'Notification id is required' };
  }

  try {
    await axiosInstance.put(`${ADMIN_NOTIFICATIONS_BASE}/${id}/read`, null, {
      params: portalParams(),
    });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to mark notification as read'),
    };
  }
};

/** PUT /admin/notifications/read — mark all notifications for a channel read. */
export const markPortalChannelNotificationsRead = async (channelId) => {
  const id = channelId ? String(channelId).trim() : '';
  if (!id) {
    return { success: false, message: 'Channel id is required' };
  }

  try {
    await axiosInstance.put(
      `${ADMIN_NOTIFICATIONS_BASE}/read`,
      { channelId: id },
      { params: portalParams() },
    );
    return { success: true, channelId: id };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to mark channel notifications as read'),
    };
  }
};

/** Route to single-notification or channel-scoped read API. */
export const markPortalNotificationReadFromAction = async (action) => {
  if (!action) {
    return { success: false, message: 'Notification id or channel id is required' };
  }

  if (action.mode === 'channel') {
    return markPortalChannelNotificationsRead(action.channelId);
  }

  return markPortalNotificationRead(action.notificationId);
};

/** Route to single-notification or channel-scoped read API. */
export const markPortalNotificationReadFromSource = async (source) => {
  const action = resolvePortalNotificationReadAction(source);

  if (!action) {
    return { success: false, message: 'Notification id or channel id is required' };
  }

  return markPortalNotificationReadFromAction(action);
};

/** PUT /admin/notifications/read-all?audience=portal */
export const markAllPortalNotificationsRead = async () => {
  try {
    await axiosInstance.put(`${ADMIN_NOTIFICATIONS_BASE}/read-all`, null, {
      params: portalParams(),
    });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to mark notifications as read'),
    };
  }
};

/** POST /admin/notifications/unregister-device */
export const unregisterNotificationDevice = async ({ token, audience }) => {
  try {
    await axiosInstance.post(`${ADMIN_NOTIFICATIONS_BASE}/unregister-device`, {
      token,
      audience: audience ?? NOTIFICATION_AUDIENCE_PORTAL,
    });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to unregister device for notifications'),
    };
  }
};
